import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Eyebrow from "../shared/Eyebrow";
import RevealText from "../shared/RevealText";

export default function BusinessIntro() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
  const y3 = useTransform(scrollYProgress, [0, 1], ["-5%", "5%"]);

  return (
    <section
      ref={ref}
      data-testid="business-intro"
      className="relative bg-ink py-24 md:py-32"
    >
      <div className="container-emilio">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Text */}
          <div className="lg:col-span-5">
            <Eyebrow>A Cleaner, Healthier Space</Eyebrow>
            <RevealText
              as="h2"
              text={"More than\nappearance."}
              className="mt-6 font-display font-extrabold text-white text-display"
            />
            <div className="mt-6 space-y-5 text-mutedtext text-base md:text-[17px] leading-relaxed max-w-md">
              <p>
                At <span className="text-white">Emilio's Solutions</span>, professional
                cleaning is about a healthier, safer and more comfortable environment —
                for homeowners, tenants, property professionals and businesses.
              </p>
              <p>
                From newly renovated homes to property handovers and commercial spaces,
                our experienced team delivers reliable cleaning with attention to every
                detail.
              </p>
            </div>
            <Link
              to="/about"
              data-testid="business-intro-about-link"
              className="mt-8 inline-flex items-center gap-2 text-emerald font-semibold text-sm hover:gap-3 transition-all"
            >
              Learn About Emilio's Solutions
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Asymmetric collage */}
          <div className="lg:col-span-7 relative">
            <div className="grid grid-cols-6 gap-4 md:gap-6">
              <motion.div
                style={{ y: y1 }}
                className="col-span-4 aspect-[4/5] overflow-hidden border border-charcoal"
              >
                <img
                  src="/generated/intro_1.png"
                  alt="Close-up of an Emilio's Solutions cleaner wiping newly installed kitchen carpentry"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
              <motion.div
                style={{ y: y2 }}
                className="col-span-2 aspect-[3/4] overflow-hidden border border-charcoal self-end"
              >
                <img
                  src="/generated/intro_2.png"
                  alt="Professional rotary floor-scrubbing machine at work"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
              <motion.div
                style={{ y: y3 }}
                className="col-span-6 aspect-[16/8] overflow-hidden border border-charcoal"
              >
                <img
                  src="/generated/intro_3.png"
                  alt="Emilio's Solutions cleaner working in a Singapore office at dusk"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
